import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { axiosPrivate } from "../../axios/axios";
import { toast } from "react-toastify";
import { color } from "../../StyleData/Config";
import { masterAPI } from "../Masters/dataConfig";
import { isFileSizeAllowed } from "../../utils/fileUploadValidation";

const importGroups = [
  {
    name: "Masters",
    items: [
      {
        path: masterAPI.import.uploadMasters,
        method: "POST",
        summary: "Import Masters Data",
      },
    ],
  },
  {
    name: "Asset",
    items: [
      {
        path: masterAPI.import.uploadLocationmaster,
        method: "POST",
        summary: "Import Location Master Data",
      },
      {
        path: masterAPI.import.uploadAssets,
        method: "POST",
        summary: "Import Assets Data",
      },
    ],
  },
  {
    name: "Work Management",
    items: [
      {
        path: masterAPI.import.uploadPmschedule,
        method: "POST",
        summary: "Import PM Schedule Data",
      },
      {
        path: masterAPI.import.uploadWorkorders,
        method: "POST",
        summary: "Import Work Order Data",
      },
    ],
  },
  {
    name: "Inventory",
    items: [
      {
        path: masterAPI.import.uploadParts,
        method: "POST",
        summary: "Import Parts Data",
      },
      {
        path: masterAPI.import.uploadStockroom,
        method: "POST",
        summary: "Import Stockroom Data",
      },
      {
        path: masterAPI.import.uploadInventory,
        method: "POST",
        summary: "Import Inventory Data",
      },
      {
        path: masterAPI.import.uploadPartreservation,
        method: "POST",
        summary: "Import Part Reservation Data",
      },
    ],
  },
];

const ImportPage = () => {
  const [loadingPath, setLoadingPath] = useState("");
  const [selectedFiles, setSelectedFiles] = useState({});

  const handleFileChange = (e, path) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!isFileSizeAllowed(file)) {
        e.target.value = "";
        return;
      }
      setSelectedFiles((prev) => ({
        ...prev,
        [path]: file,
      }));
    } else {
      setSelectedFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[path];
        return newFiles;
      });
    }
  };

  const handleUpload = async (path) => {
    const file = selectedFiles[path];
    if (!file) {
      toast.warning("Please select a file to upload");
      return;
    }

    setLoadingPath(path);
    const formData = new FormData();
    formData.append("file", file); // Adjust "file" key if your backend expects a different form-data key

    try {
      const res = await axiosPrivate.post(path, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      // Clear file after success
      setSelectedFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[path];
        return newFiles;
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPath("");
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          color: color.masterPageTitleColor,
          fontSize: color.masterPageTitleFontSize,
          fontWeight: color.masterPageTitleFontWeight,
          mb: color.masterPageTitlemd,
        }}
      >
        Import Data
      </Typography>

      {importGroups.map((group, index) => (
        <Box key={index} sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: "1.2rem",
              fontWeight: 600,
              mb: 1,
              color: "#333",
              borderBottom: "1px solid #e0e0e0",
              pb: 0.5,
            }}
          >
            {group.name}
          </Typography>

          {group.items.map((item, idx) => (
            <Accordion
              key={idx}
              disableGutters
              elevation={0}
              sx={{
                mb: 1,
                border: "1px solid #e0e0e0",
                borderRadius: "4px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{ color: color?.TableButtonColor || "#2362B4" }}
                  />
                }
                sx={{
                  bgcolor: "#f9f9f9",
                  minHeight: "48px",
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    justifyContent: "space-between",
                    m: 0,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "1rem",
                      color: "#3b4151",
                    }}
                  >
                    {item.summary}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  bgcolor: "#fafafa",
                  borderTop: "1px solid #e0e0e0",
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    borderLeft:
                      "3px solid " + (color?.TableButtonColor || "#2362B4"),
                    pl: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, mb: 2, color: "#3b4151" }}>
                    Select a CSV/Excel file to Import
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Box
                        sx={{
                          border: "1px dashed #ccc",
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            color: selectedFiles[item.path] ? "#333" : "#999",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            mr: 2,
                          }}
                        >
                          {selectedFiles[item.path]
                            ? selectedFiles[item.path].name
                            : "No file chosen"}
                        </Typography>
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          startIcon={<FileUploadOutlinedIcon />}
                        >
                          Choose File
                          <input
                            type="file"
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            hidden
                            onChange={(e) => handleFileChange(e, item.path)}
                          />
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        onClick={() => handleUpload(item.path)}
                        disabled={
                          loadingPath === item.path || !selectedFiles[item.path]
                        }
                        fullWidth
                        sx={{
                          bgcolor: color?.TableButtonColor || "#2362B4",
                          color: "white",
                          height: "100%",
                          py: 1.5,
                          "&:hover": {
                            bgcolor: color?.TableButtonColor || "#2362B4",
                            opacity: 0.9,
                          },
                          "&.Mui-disabled": {
                            bgcolor: "rgba(35, 98, 180, 0.4)",
                            color: "white",
                          },
                        }}
                      >
                        {loadingPath === item.path
                          ? "Uploading..."
                          : "Upload / Execute"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default ImportPage;
